import { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, Search, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';

export default function LinkPanel({
  parentType,
  parentId,
  linkedType,
  fetchAll,
  fetchLinked,
  labelKey = 'name',
  title,
  icon: Icon,
  searchPlaceholder,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [linkedIds, setLinkedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    if (!open || !parentId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([fetchAll(), fetchLinked(parentId)])
      .then(([all, linked]) => {
        if (cancelled) return;
        setAllItems(all || []);
        setLinkedIds(new Set((linked || []).map((item) => item.id)));
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, parentId, fetchAll, fetchLinked]);

  const filtered = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter((item) =>
      String(item[labelKey] || '').toLowerCase().includes(q)
    );
  }, [allItems, search, labelKey]);

  const handleToggle = useCallback(async (item) => {
    const isLinked = linkedIds.has(item.id);
    const body = { [parentType]: parentId, [linkedType]: item.id };

    setTogglingId(item.id);
    // Optimistic update
    setLinkedIds((prev) => {
      const next = new Set(prev);
      if (isLinked) next.delete(item.id);
      else next.add(item.id);
      return next;
    });

    try {
      if (isLinked) {
        await api.unlinkPermission(body);
      } else {
        await api.linkPermission(body);
      }
    } catch (err) {
      // Revert optimistic update on failure
      setLinkedIds((prev) => {
        const next = new Set(prev);
        if (isLinked) next.add(item.id);
        else next.delete(item.id);
        return next;
      });
      console.error('Permission toggle failed:', err);
    } finally {
      setTogglingId(null);
    }
  }, [linkedIds, parentType, parentId, linkedType]);

  return (
    <div className="border border-slate-200/80 dark:border-slate-800 rounded-2xl overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 flex items-center justify-between bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className="text-slate-400" />}
          <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
            {title}
          </span>
          {!loading && open && (
            <span className="text-[10px] text-slate-400 ms-1">
              ({linkedIds.size})
            </span>
          )}
        </div>
        <ChevronDown
          size={14}
          className={`text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Expanded content */}
      {open && (
        <div className="border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
          {/* Search */}
          <div className="relative px-4 py-2">
            <Search size={14} className="absolute start-7 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full ps-9 pe-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 placeholder:text-slate-400"
            />
          </div>

          {/* List */}
          <div className="px-4 pb-3">
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 size={18} className="animate-spin text-slate-400" />
              </div>
            ) : error ? (
              <p className="text-sm text-slate-400 text-center py-4">
                {t('settings.linkError')}
              </p>
            ) : allItems.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">
                {t('settings.linkEmpty')}
              </p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">
                {t('settings.noResults')}
              </p>
            ) : (
              <div className="space-y-0.5 max-h-48 overflow-y-auto">
                {filtered.map((item) => (
                  <label
                    key={item.id}
                    className="flex items-center gap-2 px-1 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                  >
                    {togglingId === item.id ? (
                      <Loader2 size={16} className="animate-spin text-slate-400 shrink-0" />
                    ) : (
                      <input
                        type="checkbox"
                        checked={linkedIds.has(item.id)}
                        onChange={() => handleToggle(item)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-500 focus:ring-blue-500/20"
                      />
                    )}
                    <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                      {item[labelKey] || `#${item.id}`}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
